import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Stack, Button, CircularProgress } from '@mui/joy';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle } from '@mui/icons-material';
import { getEmployerMeeting } from '../../apiconfig/api';
import { useEmployer } from '../../context/EmployerContext';
import AddToCalendarButton from '../../components/common/AddToCalendarButton';

interface MeetingData {
  id: string;
  scheduledAt: string;
  duration: number;
  location: string;
  notes?: string;
  timezone: string;
  applicants: Array<{
    user: {
      fullName: string;
    };
  }>;
  employer: {
    companyName: string;
  };
}

const MeetingSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams<{ meetingId: string }>();
  const { employer, loading: employerLoading } = useEmployer();
  const [meetingData, setMeetingData] = useState<MeetingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!meetingId) {
        setError('No meeting ID provided');
        setLoading(false);
        return;
      }

      // Wait for employer data to be loaded
      if (employerLoading) {
        return;
      }

      if (!employer?.uuid) {
        setError('Employer information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await getEmployerMeeting(employer.uuid, meetingId);
        setMeetingData(response.data.meeting);
      } catch (err: any) {
        console.error('Error fetching meeting data:', err);
        setError(err.message || 'Failed to load meeting data');
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingData();
  }, [meetingId, employerLoading, employer?.uuid]);

  const formatTimeRange = (dateString: string, durationMs: number) => {
    const startDate = new Date(dateString);
    const endDate = new Date(startDate.getTime() + durationMs);
    
    const formatTime = (date: Date) => {
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    const formatDate = (date: Date) => {
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    const date = formatDate(startDate);

    return `${date} between ${startTime} and ${endTime}`;
  };

  const handleViewMeetings = () => {
    navigate('/hiring/meetings');
  };

  const handleScheduleAnother = () => {
    navigate('/hiring/candidates');
  };

  if (loading || employerLoading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <CircularProgress size="lg" sx={{ mb: 2 }} />
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Loading meeting details...
            </Typography>
            <Typography level="body-md" sx={{ color: '#7B7F85', mt: 1 }}>
              Please wait while we fetch your meeting information.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !meetingData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
              Meeting Not Found
            </Typography>
            <Typography level="body-md" sx={{ color: '#7B7F85', mb: 3 }}>
              {error || 'The meeting you\'re looking for could not be found.'}
            </Typography>
            <Button
              variant="solid"
              onClick={handleViewMeetings}
              sx={{
                backgroundColor: '#2e2d7e',
                '&:hover': { backgroundColor: '#1e1d6e' }
              }}
            >
              View All Meetings
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4f4f4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      <Container maxWidth="md">
        <Box sx={{ 
          bgcolor: 'white', 
          borderRadius: '16px', 
          p: 4, 
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <Box sx={{ 
            width: '64px', 
            height: '64px', 
            borderRadius: '50%', 
            bgcolor: '#10B981', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}>
            <CheckCircle sx={{ color: 'white', fontSize: '2rem' }} />
          </Box>
          
          <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
            Meeting Scheduled!
          </Typography>
          
          <Typography level="h4" sx={{ fontWeight: 500, color: '#7B7F85', mb: 0 }}>
            Text message invitations have been sent to all applicants.
          </Typography>

          <Typography level="h4" sx={{ color: '#7B7F85', mb: 3, fontWeight: 500 }}>
            You'll see their responses on your meetings page.
          </Typography>

          <Box sx={{ 
            bgcolor: '#F8F9FA', 
            borderRadius: '12px', 
            p: 3, 
            mb: 3,
            textAlign: 'left'
          }}>
            <Stack spacing={2}>
              <Box>
                <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
                  Meeting Details
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  <strong>Company:</strong> {meetingData.employer.companyName}
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  <strong>Date & Time:</strong> {formatTimeRange(meetingData.scheduledAt, meetingData.duration)}
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  <strong>Location:</strong> {meetingData.location}
                </Typography>
                <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                  <strong>Applicants:</strong> {meetingData.applicants.length} candidate{meetingData.applicants.length !== 1 ? 's' : ''}
                </Typography>
                {meetingData.applicants.length > 0 && (
                  <Typography level="body-sm" sx={{ color: '#7B7F85', mt: 1 }}>
                    {meetingData.applicants.map(applicant => applicant.user.fullName).join(', ')}
                  </Typography>
                )}
              </Box>

              {meetingData.notes && (
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 500, color: '#1A1B1D', mb: 1 }}>
                    Additional Notes:
                  </Typography>
                  <Typography level="body-sm" sx={{ color: '#7B7F85' }}>
                    {meetingData.notes}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                  Add to
                </Typography>
                                 <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="flex-start" flexWrap="wrap">
                   <AddToCalendarButton variant="google" meetingData={meetingData} />
                   <AddToCalendarButton variant="apple" meetingData={meetingData} />
                   <AddToCalendarButton variant="microsoft" meetingData={meetingData} />
                 </Stack>
              </Box>
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={handleViewMeetings}
              sx={{
                borderColor: '#2e2d7e',
                color: '#2e2d7e',
                '&:hover': {
                  borderColor: '#1e1d6e',
                  backgroundColor: '#F8F9FA'
                }
              }}
            >
              View All Meetings
            </Button>
            <Button
              variant="solid"
              onClick={handleScheduleAnother}
              sx={{
                backgroundColor: '#2e2d7e',
                '&:hover': { backgroundColor: '#1e1d6e' }
              }}
            >
              Schedule Another Meeting
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default MeetingSuccess; 